import { useState, useEffect } from 'react';
import { notificationApi } from '../api.js';
import type { NotificationPreference } from '../types.js';

/** All user-facing strings of the panel. Override any subset via the `labels` prop (e.g. for i18n). */
export interface NotificationPreferencesLabels {
  title: string;
  loading: string;
  sponsorBadge: string;
  allNotifications: string;
  sponsoredOnly: string;
}

export const defaultNotificationPreferencesLabels: NotificationPreferencesLabels = {
  title: 'Notification Preferences',
  loading: 'Loading preferences...',
  sponsorBadge: 'Sponsor',
  allNotifications: 'All notifications',
  sponsoredOnly: 'Sponsored only',
};

export interface NotificationPreferencesProps {
  /** Override any user-facing string (e.g. for i18n). */
  labels?: Partial<NotificationPreferencesLabels>;
}

export function NotificationPreferences({ labels }: NotificationPreferencesProps = {}) {
  const L: NotificationPreferencesLabels = { ...defaultNotificationPreferencesLabels, ...labels };
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notificationApi.getPreferences()
      .then(data => setPreferences(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (pref: NotificationPreference) => {
    const newEnabled = !pref.enabled;
    setPreferences(prev =>
      prev.map(p => p.notification_type === pref.notification_type ? { ...p, enabled: newEnabled } : p)
    );
    try {
      await notificationApi.updatePreference(pref.notification_type, newEnabled, pref.sub_option ?? undefined);
    } catch {
      setPreferences(prev =>
        prev.map(p => p.notification_type === pref.notification_type ? { ...p, enabled: !newEnabled } : p)
      );
    }
  };

  const handleSubOption = async (pref: NotificationPreference, subOption: string) => {
    setPreferences(prev =>
      prev.map(p => p.notification_type === pref.notification_type ? { ...p, sub_option: subOption } : p)
    );
    try {
      await notificationApi.updatePreference(pref.notification_type, pref.enabled, subOption);
    } catch {
      setPreferences(prev =>
        prev.map(p => p.notification_type === pref.notification_type ? { ...p, sub_option: pref.sub_option } : p)
      );
    }
  };

  if (loading) {
    return <div className="card"><p className="text-muted">{L.loading}</p></div>;
  }

  return (
    <div className="card">
      <h3>{L.title}</h3>
      <div className="flex flex-column gap-sm">
        {preferences.map(pref => (
          <div key={pref.notification_type} className="flex flex-column gap-xs" style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border-color, #eee)' }}>
            <div className="flex align-center gap-sm">
              <label className="flex align-center gap-sm" style={{ flex: 1, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={pref.enabled}
                  onChange={() => handleToggle(pref)}
                />
                <span>{pref.label}</span>
                {pref.sponsor_only && <span className="badge">{L.sponsorBadge}</span>}
              </label>
            </div>
            <div className="text-sm text-muted" style={{ marginLeft: '1.5rem' }}>
              {pref.description}
            </div>
            {pref.has_sub_option && pref.enabled && (
              <div style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                <select
                  className="text-sm"
                  value={pref.sub_option || 'all'}
                  onChange={(e) => handleSubOption(pref, e.target.value)}
                >
                  <option value="all">{L.allNotifications}</option>
                  <option value="sponsored_only">{L.sponsoredOnly}</option>
                </select>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
